import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  FieldCheckboxGroup,
  FieldRadioButton,
} from '../../components';

import css from './SignupForm.module.css';

const KEY_CODE_ENTER = 13;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    initialValues={{ userType: 'client' }}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        onOpenTermsOfService,
        values,
        location,
        referralLink
      } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'SignupForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'SignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'SignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'SignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordLabel = intl.formatMessage({
        id: 'SignupForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'SignupForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // firstName
      const firstNameLabel = intl.formatMessage({
        id: 'SignupForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'SignupForm.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'SignupForm.firstNameRequired',
      });
      const firstNameRequired = validators.required(firstNameRequiredMessage);

      // lastName
      const lastNameLabel = intl.formatMessage({
        id: 'SignupForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'SignupForm.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'SignupForm.lastNameRequired',
      });
      const lastNameRequired = validators.required(lastNameRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const providerValidation = values.userType === 'service-provider' && !values.providerType;
      const submitDisabled = submitInProgress || providerValidation;

      const handleTermsKeyUp = e => {
        // Allow click action with keyboard like with normal links
        if (e.keyCode === KEY_CODE_ENTER) {
          onOpenTermsOfService();
        }
      };
      const termsLink = (
        <span
          className={css.termsLink}
          onClick={onOpenTermsOfService}
          role="button"
          tabIndex="0"
          onKeyUp={handleTermsKeyUp}
        >
          <FormattedMessage id="SignupForm.termsAndConditionsLinkText" />
        </span>
      );


      const rootUrl = process.env.REACT_APP_CANONICAL_ROOT_URL;
      const referralLinkLabelMessage = intl.formatMessage({
        id: 'SignupForm.referralLinkLabel',
      });
      const referralLinkPlaceholderMessage = intl.formatMessage(
        {
          id: 'SignupForm.referralLinkPlaceholder',
        },
        {
          rootUrl,
        }
      );

      const referralLinkValidMessage = intl.formatMessage({
          id: 'SignupForm.referralLinkValid',
        },
        {
          lineBreak: <br />,
          rootUrl
        }
      );

      const userTypeOptions = [
        {
          key: 'client',
          label: 'I am looking for a Company',
        },
        {
          key: 'service-provider',
          label: 'I want to list my Company',
        },
      ];

      const isClientSelected = values.userType === 'client';

      const contractLink = isClientSelected ? (
        <a href="https://semoto-bucket.s3.eu-west-2.amazonaws.com/1741009284726.pdf"
           target="_blank">Contract</a>
      ) : (
        <a href="https://semoto-bucket.s3.eu-west-2.amazonaws.com/1741009272747.pdf"
           target="_blank">Contract</a>
      )

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <FieldSelect id="userType" name={'userType'} label={'I am'} className={css.selectField}>
              {userTypeOptions.map(({ key, label }) => {
                return (
                  <option key={key} value={key}>
                    {label}
                  </option>
                );
              })}
            </FieldSelect>

            {values.userType === 'service-provider' && (
              <div className={css.checkboxesField}>
                <label className={css.isRequired}>
                  {intl.formatMessage({
                    id: 'EditListingDescriptionForm.providerType',
                  })}
                </label>
                <FieldRadioButton
                  id="providerTypeEstablished"
                  name="providerType"
                  label="Established Company"
                  value="establishedCompany"
                />
                <FieldRadioButton
                  id="providerTypeStartup"
                  name="providerType"
                  label="Startup"
                  value="startup"
                  tipId="EditListingDescriptionForm.providerTypeTip"
                />
              </div>
            )}

            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.fname` : 'fname'}
                name="fname"
                autoComplete="given-name"
                label={'Name'}
                placeholder={firstNamePlaceholder}
                validate={firstNameRequired}
                isRequired
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lname` : 'lname'}
                name="lname"
                autoComplete="family-name"
                label={'Surname'}
                placeholder={lastNamePlaceholder}
                validate={lastNameRequired}
                isRequired
              />
            </div>

            <FieldTextInput
              className={css.textField}
              type="text"
              id={formId ? `${formId}.companyName` : 'companyName'}
              name="companyName"
              label={'Company name'}
              placeholder={'Enter your company name...'}
              // validate={lastNameRequired}
            />

            <FieldTextInput
              className={css.textField}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
              isRequired
            />

            <FieldTextInput
              className={css.textField}
              type="text"
              id={formId ? `${formId}.website` : 'website'}
              name="website"
              label={'Website • optional'}
              placeholder={'www.example.com'}
              // validate={lastNameRequired}
            />

            <FieldTextInput
              className={css.textField}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="new-password"
              label={passwordLabel}
              placeholder={passwordPlaceholder}
              validate={passwordValidators}
              isRequired
            />

            <FieldTextInput
              className={classNames(css.textField, { [css.textFieldBlocked]: !!referralLink })}
              type="text"
              id={formId ? `${formId}.referralLink` : 'referralLink'}
              name="referralLink"
              label={referralLinkLabelMessage}
              placeholder={referralLinkPlaceholderMessage}
              defaultValue={!!referralLink ? referralLink : null}
              validate={validators.validUserIdFormat(referralLinkValidMessage)}
            />
          </div>

          <div className={css.bottomWrapper}>
            <p className={css.bottomWrapperText}>
              <span className={css.termsText}>

                <FieldCheckboxGroup
                  name="terms"
                  id="terms-accepted"
                  optionLabelClassName={css.finePrint}
                  options={[
                    {
                      key: 'tos-and-privacy',
                      label: intl.formatMessage(
                        { id: 'SignupForm.termsAndConditionsAcceptText' },
                        { contractLink }
                      ),
                    },
                  ]}
                  validate={validators.requiredFieldArrayCheckbox(
                    intl.formatMessage({ id: 'SignupForm.termsAndConditionsAcceptRequired' })
                  )}
                />
              </span>
            </p>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

const { bool, func } = PropTypes;

SignupFormComponent.propTypes = {
  inProgress: bool,

  onOpenTermsOfService: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
